// eslint-disable-next-line no-restricted-imports -- approved output-boundary shim for billboard consumers
import { List, Map } from 'immutable';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { getFilledAlarmStates } from '@sophocles/entities/AlarmState';
import { addRadiosToCIC, getCICSelector } from '@sophocles/entities/CameraInterfaceController';
import { intrusionSensorConfigurationSelector } from '@sophocles/entities/IntrusionSensorConfiguration';
import { radioSelector } from '@sophocles/entities/Radio';
import {
  addRelayLoadTypeToRelays,
  addRelayLoadsToRelays,
  addSchedulesToRelays,
  associateRelaysAndRemoveThem,
  relaySelector,
} from '@sophocles/entities/Relay';
import { relayControllerFirmwareVersionSelector } from '@sophocles/entities/RelayControllerFirmwareVersion';
import {
  addLightFixtureToRelayLoads,
  addRelayLoadContractsToRelayLoads,
  relayLoadSelector,
} from '@sophocles/entities/RelayLoad';
import { relayLoadContractSelector } from '@sophocles/entities/RelayLoadContract';
import { relayScheduleSelector } from '@sophocles/entities/RelaySchedule';
import {
  addAlarmsToSites,
  addCicsToSites,
  addSiteConfigurationsToSites,
  addSiteMaintenanceLogsToSite,
  addSmartlinksToSites,
  addWeatherConfigurationToSites,
  siteSelector,
} from '@sophocles/entities/Site';
import { siteConfigurationSelector } from '@sophocles/entities/SiteConfiguration';
import { addUsersToSiteMaintenanceLogs, siteMaintenanceLogSelector } from '@sophocles/entities/SiteMaintenanceLog';
import { siteWeatherConfigurationSelector } from '@sophocles/entities/SiteWeatherConfiguration';
import {
  addCICToSmartlink,
  addFirmwareVersionToSmartlink,
  addIntrusionSensorToSmartlink,
  addModelToSmartlink,
  addRadiosToSmartlink,
  addRelaysToSmartlink,
  smartlinkUnitSelector,
} from '@sophocles/entities/SmartlinkUnit';
import { userSelector } from '@sophocles/entities/User';
import { selectUnitModelsAsImmutableMap } from '@sophocles/features/reference';

export const selectedStructure = state => state.selectedStructure.selectedStructure;
export const extraStructureFields = state => state.selectedStructure.extraStructureFields;
export const extraRelayFields = state => state.selectedStructure.extraRelayFields;
export const receivedEntities = state => state.selectedStructure.receivedEntities;
export const scheduleViewSelector = state => state.selectedStructure.scheduleView ?? 'list';

// filters
// const filteredSites = filterSitesById(siteSelector, SelectedStructure);
// const filteredSmartlinks = filterSmartlinksBySites(smartlinkUnitSelector, filteredSites);

// relayLoadType to load
// const relayLoadsWithType = addRelayLoadTypeToRelayLoads(relayLoadSelector);

// relayLoad + light fixture
const relayLoadsWithFixture = addLightFixtureToRelayLoads(relayLoadSelector);

// add contracts to relayloads
const relayLoadsWithFixtureAndContract = addRelayLoadContractsToRelayLoads(
  relayLoadsWithFixture,
  relayLoadContractSelector,
);

const relaysWithLoadType = addRelayLoadTypeToRelays(relaySelector, '1'); // include default value of 1 for static!
// relays + loads
const relays = addRelayLoadsToRelays(relaysWithLoadType, relayLoadsWithFixtureAndContract);
// + relay schedules
const relaysWithSchedules = addSchedulesToRelays(relays, relayScheduleSelector);

const associatedRelays = associateRelaysAndRemoveThem(relaysWithSchedules);

// cics + radios
const cicsWithRadios = addRadiosToCIC(getCICSelector, radioSelector);

// smartlinks + radios
const smartlinks = addRadiosToSmartlink(smartlinkUnitSelector, radioSelector);

// smartlinks + firmware version
const smartlinksWithVersion = addFirmwareVersionToSmartlink(smartlinks, relayControllerFirmwareVersionSelector);

// smartlinks + cic
const smartlinksWithCICs = addCICToSmartlink(smartlinksWithVersion, getCICSelector);
// combine smartlinks + relays
const smartlinksWithRelays = addRelaysToSmartlink(smartlinksWithCICs, associatedRelays);
// + smartlinkUnitModels
const smartlinksWithRelaysAndModal = addModelToSmartlink(smartlinksWithRelays, selectUnitModelsAsImmutableMap);

const finalSmartlinks = addIntrusionSensorToSmartlink(
  smartlinksWithRelaysAndModal,
  intrusionSensorConfigurationSelector,
);

// sites + cics
const sitesWithCics = addCicsToSites(siteSelector, cicsWithRadios);

// sites + smartlinks
const sitesWithSmartlinks = addSmartlinksToSites(sitesWithCics, finalSmartlinks);

// + configurations
const sitesWithConfigurations = addSiteConfigurationsToSites(sitesWithSmartlinks, siteConfigurationSelector);
// + alarmstates
const getSitesWithAlarms = addAlarmsToSites(sitesWithConfigurations, getFilledAlarmStates);

// const getSitesWithAlerts = addSiteAlertsToSites(getSitesWithAlarms, getFilledSiteAlerts);

// siteMaintenanceLogs + users
const getMaintenanceLogs = addUsersToSiteMaintenanceLogs(siteMaintenanceLogSelector, userSelector);
// sites + siteMaintenanceLogs
const getSitesSelector = addWeatherConfigurationToSites(
  addSiteMaintenanceLogsToSite(getSitesWithAlarms, getMaintenanceLogs),
  siteWeatherConfigurationSelector,
);

export const getInitialSelectedStructure = createSelector(getSitesSelector, selectedStructure, (sites, selected) =>
  sites.get(selected.toString(), Map()),
);

export const getSelectedStructure = createSelector(getInitialSelectedStructure, site => {
  site = Map(site); // convert to Map

  const customData = _.mapKeys(site.get('custom_data', {}), (v, k) => 'user_' + k);

  // create additional field mapping
  const flatMap = Map({
    tag_number: site.getIn(['configuration', 'tag_number'], ''),
    utility_meter_number: site.getIn(['configuration', 'utility_meter_number'], ''),
    utility_company: site.getIn(['configuration', 'utility_company'], ''),
    utility_account_number: site.getIn(['configuration', 'utility_account_number'], ''),
    // Add in custom structure field data here.
  });

  // merge the flatMap to make a flatter structure object
  site = site.merge(customData);
  return site.merge(flatMap);
});

export const selectSite = siteId =>
  createSelector(
    state => siteId,
    sitesWithConfigurations,
    (siteId, sites) => {
      return sites.toJS()[siteId];
    },
  );

export const getSelectedStructureUnit = createSelector(getInitialSelectedStructure, site => {
  const siteMap = Map(site);

  const selectedUnit = {
    deviceType: null,
    unit: Map(),
  };

  const relayController = siteMap.get('relay_controller', null);
  const cic = siteMap.get('cic', null);

  if (relayController) {
    selectedUnit.unit = relayController;
    selectedUnit.deviceType = 'relay_controller';
  } else if (cic) {
    selectedUnit.unit = cic;
    selectedUnit.deviceType = 'cic';
  }

  return selectedUnit;
});

export const getStructureAlerts = createSelector(getSelectedStructure, site => site.get('alerts', List()));
